import { useMemo, useState, type DragEvent } from 'react';
import { Search } from 'lucide-react';
import {
  NODE_CATEGORIES,
  CATEGORY_LABELS,
  getAllNodeDefinitions,
  getNodesByCategory,
} from '../registry/node-registry';
import { useEditorStore } from '../../state/editor-store';

export const NODE_DRAG_MIME = 'application/x-dark-funnels-node-type';

export function NodePalette() {
  const [query, setQuery] = useState('');
  const addNode = useEditorStore((state) => state.addNode);
  const matches = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return new Set(getAllNodeDefinitions().map((item) => item.type));
    return new Set(
      getAllNodeDefinitions()
        .filter((item) =>
          [item.label, item.type, ...item.keywords].some((value) =>
            value.toLocaleLowerCase().includes(needle),
          ),
        )
        .map((item) => item.type),
    );
  }, [query]);
  const dragStart = (event: DragEvent, type: string) => {
    event.dataTransfer.setData(NODE_DRAG_MIME, type);
    event.dataTransfer.effectAllowed = 'copy';
  };
  return (
    <aside
      className="w-56 shrink-0 overflow-y-auto border-r border-border-subtle bg-surface p-3"
      aria-label="Paleta de nós"
    >
      <label className="relative block">
        <span className="sr-only">Buscar nós</span>
        <Search aria-hidden size={14} className="absolute left-2 top-2.5 text-text-muted" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar nós"
          className="w-full rounded-md border border-border bg-surface-2 py-2 pl-8 pr-2 text-xs text-text-primary outline-none"
        />
      </label>
      <div className="mt-3 space-y-4">
        {NODE_CATEGORIES.map((category) => {
          const items = getNodesByCategory(category).filter((item) => matches.has(item.type));
          if (!items.length) return null;
          return (
            <section key={category} aria-labelledby={`palette-${category}`}>
              <h3
                id={`palette-${category}`}
                className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted"
              >
                {CATEGORY_LABELS[category]}
              </h3>
              <div className="space-y-1">
                {items.map((item) => (
                  <button
                    key={item.type}
                    type="button"
                    aria-label={`Adicionar ${item.label}`}
                    draggable
                    onDragStart={(event) => dragStart(event, item.type)}
                    onClick={() => addNode(item.type)}
                    className="flex w-full items-center gap-2 rounded-md border border-border-subtle px-2 py-1.5 text-left text-xs text-text-secondary hover:border-border hover:bg-surface-2 hover:text-text-primary"
                    style={{ borderLeftColor: `var(--node-${item.category})`, borderLeftWidth: 3 }}
                    title={`Adicionar ${item.label}`}
                  >
                    <span>{item.label}</span>
                    <span className="ml-auto font-mono text-[9px] text-text-muted">+</span>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
        {matches.size === 0 ? <p className="text-xs text-text-muted">Nenhum nó correspondente.</p> : null}
      </div>
    </aside>
  );
}
